import { HTMLAttributes } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { CardRarity, Rarity, Sport } from '__generated__/globalTypes';
import { proxyUrl } from 'atoms/ui/ResponsiveImg';
import Shine from 'atoms/ui/Shine';
import { FRONTEND_ASSET_HOST } from 'constants/assets';
import { getScarcityName } from 'lib/baseball';
import { CARD_BORDER_RADIUS, CARD_SIZE } from 'lib/cards';

const CardShine = styled(Shine)`
  aspect-ratio: var(--card-aspect-ratio);
`;

const IMG = styled.img`
  width: 240px;
  aspect-ratio: var(--card-aspect-ratio);
  max-width: 100%;
  max-height: 100%;
  vertical-align: bottom;
  /* embed don't accept onclick, so we bypass the element */
  pointer-events: none;
`;

const pathBySport = {
  [Sport.BASEBALL]: 'mlb',
  [Sport.NBA]: 'nba',
};

// To add sport: make sure to create .svg component in the designated sport package
export type Props = {
  sport: Sport.BASEBALL | Sport.NBA;
  scarcity: CardRarity | Rarity;
  tier?: number;
  shine?: boolean;
  season?: string;
} & HTMLAttributes<HTMLElement>;

export const getCardBackUrl = (
  sport: Sport.BASEBALL | Sport.NBA,
  scarcity: CardRarity | Rarity,
  tier?: number,
  season: string = '2023'
) => {
  const urlParts = [FRONTEND_ASSET_HOST, 'cards/back', pathBySport[sport]];
  if (season !== '2022') {
    urlParts.push(season);
  }
  urlParts.push(`${scarcity}_tier_${tier === undefined ? 'none' : tier}.png`);
  return urlParts.join('/');
};

export const USSportCardBack = ({
  sport,
  scarcity,
  tier,
  season = '2023',
  shine,
  className,
  ...divAttributes
}: Props) => {
  const { formatMessage } = useIntl();

  const url = getCardBackUrl(sport, scarcity, tier, season);
  return (
    <CardShine
      className={className}
      borderRadius={CARD_BORDER_RADIUS}
      disabled={!shine}
      title={
        tier !== undefined
          ? formatMessage(
              {
                id: 'CardBack.titleWithTier',
                defaultMessage: '{scarcity} Card, tier {tier}',
              },
              { scarcity: getScarcityName(scarcity), tier }
            )
          : formatMessage(
              {
                id: 'CardBack.title',
                defaultMessage: '{scarcity} Card',
              },
              { scarcity }
            )
      }
      {...divAttributes}
    >
      <IMG src={proxyUrl(url, { cropWidth: CARD_SIZE })} />
    </CardShine>
  );
};
